import React from 'react'
import { produce } from 'immer'
import store from '../../../../store'

import _set from 'lodash/set'
import { createStore } from 'redux'
import { Provider, useDispatch, useSelector, useStore } from 'react-redux'
import { UserClassificationInfoRecord, UserClassificationPageState } from './types'
import { nanoid } from 'nanoid'
import { padString } from './helpers'
import { z } from 'zod/mini'
import { formatDateCustom, parseDateCustom } from '../../../../common/utils/DateUtils'

const appState = store.getState

const initState = UserClassificationPageState.parse({
  mainStore: appState(),
  userClassificationTypes: [],
  selectedUserClassificationType: '',
  userClassificationRecords: {},
  changeHistory: [],
  pendingUpdates: {},
  updateStatus: {},
  importedFlags: {},
  errors: {
    server: [],
    client: [],
    byClassification: {},
    importWarnings: {},
    importValidationErrors: {},
    recordValidationErrors: {}
  }
})

export const userClassificationReducer = (state = initState, action) => {
  const findSelectedRecords = (draft) => draft.userClassificationRecords[draft.selectedUserClassificationType]

  const findRecord = (draft, key, field = 'key') => {
    return findSelectedRecords(draft).find((e) => e[field] === key)
  }

  return produce(state, (draft) => {
    const { type, payload } = action
    switch (type) {
      case 'INIT_STATE': {
        const { types, records, changeHistory } = payload

        // 変更情報を初期化
        draft.pendingUpdates = {}
        draft.updateStatus = {}
        draft.importedFlags = {}

        // エラー情報を初期化（画面更新・遷移時に区分ごとのエラー情報を破棄）
        draft.errors = {
          server: [],
          client: [],
          byClassification: {},
          importWarnings: {},
          importValidationErrors: {},
          recordValidationErrors: {}
        }

        // 初期状態をリセット（画面更新・遷移時に初期状態を破棄）
        draft.initialClassificationRecords = {}

        // itemCountを含む型でパース（APIが { types, changeHistory } を返す前提。undefinedの場合は空配列）
        const typesArray = Array.isArray(types) ? types : []
        draft.userClassificationTypes = UserClassificationPageState.shape.userClassificationTypes.parse(typesArray)

        // 変更履歴を保存
        if (changeHistory) {
          draft.changeHistory = changeHistory
        }

        draft.selectedUserClassificationType = draft.userClassificationTypes?.[0]?.code ?? ''

        const tempClassificationRecords = Object.fromEntries(draft.userClassificationTypes.map((e) => [e.code, []]))

        for (const record of records) {
          tempClassificationRecords?.[record.type]?.push(record)
        }

        draft.userClassificationRecords =
          UserClassificationPageState.shape.userClassificationRecords.parse(tempClassificationRecords)

        // 初期状態を保存（深いコピー）- レコードが存在する場合のみ
        if (
          Object.keys(draft.userClassificationRecords).some((key) => draft.userClassificationRecords[key].length > 0)
        ) {
          draft.initialClassificationRecords = JSON.parse(JSON.stringify(draft.userClassificationRecords))
        }

        break
      }
      case 'SET_STATE': {
        const { path, value } = payload
        _set(draft, path, value)
        break
      }

      case 'SET_CLASSIFICATION_RECORDS': {
        // 区分タイプの区分項目を設定
        const { classificationType, records } = payload
        if (!draft.userClassificationRecords[classificationType]) {
          draft.userClassificationRecords[classificationType] = []
        }

        // APIレスポンスを画面データに変換
        const convertedRecords = records.map((record) => {
          const parseDate = (dateStr) => {
            if (!dateStr || dateStr === '') return null
            return parseDateCustom(dateStr, 'yyyy/MM/dd')
          }

          return UserClassificationInfoRecord.parse({
            key: record.key,
            code: record.code,
            value: record.value,
            rank: record.rank,
            validStartDate: parseDate(record.valid_start_date),
            validEndDate: parseDate(record.valid_end_date),
            memo: record.memo || '',
            status: record.status === '使用中' ? '使用中' : '未使用',
            type: record.type,
            displayOrder: record.display_order
          })
        })

        draft.userClassificationRecords[classificationType] = convertedRecords
        break
      }

      case 'ADD_CLASSIFICATION': {
        const selectedClassificationType = draft.userClassificationRecords[draft.selectedUserClassificationType]

        // 削除されていないレコードの最大displayOrderを取得
        const activeRecords = selectedClassificationType.filter((record) => !record.isDeleted)
        let maxDisplayOrder = 0
        if (activeRecords.length > 0) {
          maxDisplayOrder = Math.max(
            ...activeRecords.map((record) => {
              const order = parseInt(record.displayOrder, 10)
              return isNaN(order) ? 0 : order
            })
          )
        }

        // 最大値+1を新しいレコードのdisplayOrderに設定
        selectedClassificationType.push(
          UserClassificationInfoRecord.parse({
            ...payload,
            type: draft.selectedUserClassificationType,
            key: nanoid(),
            displayOrder: padString(maxDisplayOrder + 1),
            isCreated: true
          })
        )

        // 区分項目追加時に、左側の区分一覧の件数を更新
        // 区分項目一覧の登録数と同じ値（削除されていない項目を含む全てのレコード数）を設定
        const typeIndex = draft.userClassificationTypes.findIndex(
          (t) => t.code === draft.selectedUserClassificationType
        )
        if (typeIndex !== -1) {
          draft.userClassificationTypes[typeIndex].itemCount = selectedClassificationType.length
        }

        break
      }

      case 'UPDATE_CLASSIFICATION': {
        const record = findRecord(draft, payload.key)
        Object.assign(record, z.partial(UserClassificationInfoRecord).parse(payload))

        // モーダルの設定押下では payload に _rawValid*（取り込み時の生値）が undefined で入る。
        // スキーマ外のキーは parse で除去されるため Object.assign では消えず、有効期間を未設定で
        // 保存すると一覧セルに取り込んだ 'abc' 等が再表示される。明示的に削除する（この行の #3231 追加対応）
        if ('_rawValidStartDate' in payload) delete record._rawValidStartDate
        if ('_rawValidEndDate' in payload) delete record._rawValidEndDate

        // isDeletedが変更された場合、新規追加項目の削除時のみ左側の区分一覧の件数を更新
        // 既存項目の削除時は件数を更新しない（削除状態は一時的なもののため）
        // 区分項目一覧の登録数と同じ値（削除されていない項目を含む全てのレコード数）を設定
        if (payload.isDeleted !== undefined && record.isCreated === true) {
          const selectedRecords = findSelectedRecords(draft)
          const typeIndex = draft.userClassificationTypes.findIndex(
            (t) => t.code === draft.selectedUserClassificationType
          )
          if (typeIndex !== -1) {
            draft.userClassificationTypes[typeIndex].itemCount = selectedRecords.length
          }
        }

        break
      }

      case 'UPDATE_DISPLAY_ORDER': {
        draft.userClassificationRecords[draft.selectedUserClassificationType] = payload.sortedArray
        break
      }

      case 'REMOVE_CLASSIFICATION': {
        const record = findRecord(draft, payload.key)
        const selectedRecords = findSelectedRecords(draft)

        const index = selectedRecords.indexOf(record)

        selectedRecords.splice(index, 1)

        // 区分項目削除時に、左側の区分一覧の件数を更新
        // 区分項目一覧の登録数と同じ値（削除されていない項目を含む全てのレコード数）を設定
        const typeIndex = draft.userClassificationTypes.findIndex(
          (t) => t.code === draft.selectedUserClassificationType
        )
        if (typeIndex !== -1) {
          draft.userClassificationTypes[typeIndex].itemCount = selectedRecords.length
        }

        break
      }

      case 'BATCH_ADD_CLASSIFICATION': {
        draft.userClassificationRecords[draft.selectedUserClassificationType] = payload.map((e, index) => ({
          ...e,
          type: draft.selectedUserClassificationType,
          displayOrder: padString(index),
          isCreated: true
        }))

        // CSVインポート時に、左側の区分一覧の件数を更新
        // 区分項目一覧の登録数と同じ値（削除されていない項目を含む全てのレコード数）を設定
        const selectedRecords = draft.userClassificationRecords[draft.selectedUserClassificationType]
        const typeIndex = draft.userClassificationTypes.findIndex(
          (t) => t.code === draft.selectedUserClassificationType
        )
        if (typeIndex !== -1) {
          draft.userClassificationTypes[typeIndex].itemCount = selectedRecords.length
        }

        break
      }
      case 'UPDATE_MAIN_STATE': {
        Object.assign(draft.mainStore, payload)
        break
      }
      case 'SAVE_INITIAL_RECORDS': {
        // 選択中の区分タイプの初期状態を保存
        const { classificationType, records } = payload
        if (!draft.initialClassificationRecords) {
          draft.initialClassificationRecords = {}
        }
        // Dateオブジェクトを文字列に変換して保存（リセット時に復元できるように）
        const serializedRecords = records.map((record) => ({
          ...record,
          validStartDate: record.validStartDate ? formatDateCustom(record.validStartDate, 'yyyy/MM/dd') : null,
          validEndDate: record.validEndDate ? formatDateCustom(record.validEndDate, 'yyyy/MM/dd') : null
        }))
        draft.initialClassificationRecords[classificationType] = JSON.parse(JSON.stringify(serializedRecords))
        break
      }
      case 'UPDATE_CLASSIFICATION_TYPE_COUNT': {
        // 区分選択時に、IF02070から取得した実際の件数でitemCountを更新（未保存の変更を破棄）
        const { classificationType, itemCount } = payload
        const typeIndex = draft.userClassificationTypes.findIndex((t) => t.code === classificationType)
        if (typeIndex !== -1) {
          draft.userClassificationTypes[typeIndex].itemCount = itemCount
        }
        break
      }
      case 'MARK_CLASSIFICATION_AS_PENDING': {
        // 区分を未反映状態にマーク
        const { classificationCode } = payload
        draft.updateStatus[classificationCode] = 'pending'
        break
      }
      case 'SAVE_PENDING_UPDATE': {
        // 更新データを保存
        const { classificationCode, updateData } = payload
        draft.pendingUpdates[classificationCode] = updateData
        break
      }
      case 'UPDATE_CLASSIFICATION_STATUS': {
        // 更新ステータスを変更
        const { classificationCode, status } = payload
        draft.updateStatus[classificationCode] = status
        break
      }
      case 'CLEAR_PENDING_UPDATES': {
        // 全ての未反映状態をクリア
        draft.pendingUpdates = {}
        draft.updateStatus = {}
        break
      }
      case 'MARK_IMPORTED': {
        // 区分をインポート済みとしてマーク
        const { classificationCode } = payload
        if (!draft.importedFlags) {
          draft.importedFlags = {}
        }
        draft.importedFlags[classificationCode] = true
        break
      }
      case 'CLEAR_IMPORTED_FLAG': {
        // 区分のインポートフラグをクリア
        const { classificationCode } = payload
        if (draft.importedFlags && draft.importedFlags[classificationCode]) {
          delete draft.importedFlags[classificationCode]
        }
        break
      }
      default: {
        break
      }
    }
  })
}

/** @typedef {import('redux').Store<typeof initState, import('redux').AnyAction>} ClassificationStore */

/** @type {() => ClassificationStore} */
export const useUserClassificationStore = useStore

export const useUserClassificationDispatch = () => {
  const dispatch = useDispatch()
  return (type, payload) => dispatch({ type, payload })
}

/** @type {import('react-redux').TypedUseSelectorHook<typeof initState>} * */
export const useUserClassificationSelector = useSelector

export const UserClassificationInfoProvider = (props) => {
  const [store] = React.useState(() => createStore(userClassificationReducer))
  return <Provider store={store}>{props.children}</Provider>
}
